import { useMemo } from "react";
import { Modal, Button } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * GrnDetailsModal — read-only drilldown for a Goods Receipt Note.
 *
 * Shows the full GRN (header + line items) and, for each line item,
 * the vendor's purchase history over the past 12 months (previous
 * GRN #, date, qty and unit cost) sourced from the already-loaded
 * `allGrns` list. The current GRN is excluded from each item's history.
 */
export default function GrnDetailsModal({ open, onClose, grn, invMap = {}, vendorMap = {}, allGrns = [] }) {
    const oneYearBeforeGrn = useMemo(() => {
        if (!grn?.created_at) return null;
        const d = new Date(grn.created_at);
        if (isNaN(d)) return null;
        d.setFullYear(d.getFullYear() - 1);
        return d;
    }, [grn]);

    const historyByItem = useMemo(() => {
        if (!grn) return {};
        const anchorIso = grn.created_at || "";
        const map = {};
        (grn.items || []).forEach((line) => {
            map[line.inventory_id] = (allGrns || [])
                .filter((g) => g.id !== grn.id)
                // Strictly earlier than the GRN being viewed (prior purchases only)
                .filter((g) => (g.created_at || "") < anchorIso)
                // Within 1 year before the viewed GRN's own date
                .filter((g) => {
                    if (!oneYearBeforeGrn) return true;
                    const dt = g.created_at ? new Date(g.created_at) : null;
                    return dt && !isNaN(dt) && dt >= oneYearBeforeGrn;
                })
                .flatMap((g) => (g.items || [])
                    .filter((it) => it.inventory_id === line.inventory_id)
                    .map((it) => ({
                        grn_number: g.grn_number,
                        created_at: g.created_at,
                        vendor_id: g.vendor_id,
                        quantity: it.quantity,
                        unit_cost: it.unit_cost,
                    }))
                )
                .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        });
        return map;
    }, [grn, allGrns, oneYearBeforeGrn]);

    if (!grn) return null;
    const vendorName = vendorMap[grn.vendor_id]?.name || grn.vendor_id || "—";
    const total = grn.total_cost || 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`${grn.grn_number} · Goods Receipt Note`}
            size="xl"
            footer={<Button variant="secondary" onClick={onClose} data-testid="grn-details-close">Close</Button>}
        >
            <div className="space-y-5" data-testid="grn-details-modal">
                {/* Header */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <Field label="GRN #" value={grn.grn_number} mono />
                    <Field label="Date" value={formatDate(grn.created_at)} />
                    <Field label="Vendor" value={vendorName} />
                    <Field label="Line items" value={(grn.items || []).length} mono />
                    {grn.notes && <div className="col-span-full"><Field label="Notes" value={grn.notes} /></div>}
                </div>

                {/* Line items + per-item history */}
                <div className="space-y-4">
                    {(grn.items || []).map((line, idx) => {
                        const inv = invMap[line.inventory_id];
                        const history = historyByItem[line.inventory_id] || [];
                        const lineTotal = (line.quantity || 0) * (line.unit_cost || 0);
                        return (
                            <div key={idx} className="border border-fleet-border rounded-md overflow-hidden"
                                 data-testid={`grn-line-${idx}`}>
                                {/* Current line */}
                                <div className="bg-slate-50 px-4 py-3 border-b border-fleet-border grid grid-cols-2 sm:grid-cols-5 gap-3">
                                    <Field label="SKU" value={inv?.sku || line.inventory_id?.slice(0, 8) || "—"} mono />
                                    <div className="sm:col-span-2">
                                        <Field label="Item" value={inv?.name || "—"} />
                                    </div>
                                    <Field label="Qty" value={`${line.quantity} ${inv?.unit || ""}`} mono />
                                    <Field label="Unit cost" value={inr(line.unit_cost)} mono />
                                    <div className="sm:col-span-5 flex justify-end">
                                        <span className="label-mono mr-2">LINE TOTAL</span>
                                        <span className="font-mono text-sm font-semibold" data-testid={`grn-line-total-${idx}`}>{inr(lineTotal)}</span>
                                    </div>
                                </div>

                                {/* History */}
                                <div className="px-4 py-3">
                                    <div className="label-mono mb-2">PURCHASE HISTORY · PAST 1 YEAR</div>
                                    {history.length === 0 ? (
                                        <div className="text-xs text-fleet-inkSoft italic" data-testid={`grn-history-empty-${idx}`}>
                                            No prior purchases in the last 12 months.
                                        </div>
                                    ) : (
                                        <table className="w-full text-xs" data-testid={`grn-history-table-${idx}`}>
                                            <thead>
                                                <tr className="text-left text-fleet-inkSoft">
                                                    <th className="font-normal py-1.5">GRN #</th>
                                                    <th className="font-normal py-1.5">Date</th>
                                                    <th className="font-normal py-1.5">Vendor</th>
                                                    <th className="font-normal py-1.5 text-right">Qty</th>
                                                    <th className="font-normal py-1.5 text-right">Unit cost</th>
                                                    <th className="font-normal py-1.5 text-right">Vs now</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {history.map((h, hi) => {
                                                    const delta = (line.unit_cost || 0) - (h.unit_cost || 0);
                                                    const pct = h.unit_cost ? (delta / h.unit_cost) * 100 : 0;
                                                    const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
                                                    const cls = dir === "up" ? "text-rose-600" : dir === "down" ? "text-emerald-600" : "text-fleet-inkSoft";
                                                    return (
                                                        <tr key={hi} className="border-t border-fleet-border">
                                                            <td className="py-1.5 font-mono">{h.grn_number}</td>
                                                            <td className="py-1.5 font-mono">{formatDate(h.created_at)}</td>
                                                            <td className="py-1.5">{vendorMap[h.vendor_id]?.name || h.vendor_id?.slice(0, 8) || "—"}</td>
                                                            <td className="py-1.5 font-mono text-right">{h.quantity}</td>
                                                            <td className="py-1.5 font-mono text-right">{inr(h.unit_cost)}</td>
                                                            <td className={`py-1.5 font-mono text-right ${cls}`}>
                                                                {dir === "flat" ? "—" : `${delta > 0 ? "+" : ""}${pct.toFixed(1)}%`}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Grand total */}
                <div className="flex justify-end items-center gap-3 pt-2 border-t border-fleet-border">
                    <span className="label-mono">GRN TOTAL</span>
                    <span className="font-mono text-lg font-semibold" data-testid="grn-details-total">{inr(total)}</span>
                </div>
            </div>
        </Modal>
    );
}

function Field({ label, value, mono = false }) {
    return (
        <div>
            <div className="label-mono mb-0.5">{label}</div>
            <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}
